import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  CheckCircle, Flag, FolderOpen, AlertTriangle, Plus, ClipboardList,
  FileText, ShieldCheck, Activity, Loader2, User, RefreshCw, ListFilter,
  CalendarDays, PlayCircle, CheckCircle2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  useAuditFindings,
  type ReviewStatus, type FindingSeverity, type FindingStatus,
  type AuditFinding, type CycleStatus, type AuditItemType,
} from "@/hooks/use-audit-findings";
import { useComplianceTasks } from "@/hooks";
import { formatFramework, useCompliancePrograms } from "@/hooks/use-compliance-programs";
import { TaskFormModal } from "@/components/compliance/TaskFormModal";
import type { ComplianceTask } from "@/hooks/use-compliance-tasks";
import { RaiseFindingModal } from "./RaiseFindingModal";
import { RequestEvidenceModal } from "./RequestEvidenceModal";
import { UpdateFindingModal } from "./UpdateFindingModal";

// ─── Badge configs ────────────────────────────────────────────────────────────

const reviewStatusConfig: Record<ReviewStatus, { label: string; className: string }> = {
  not_reviewed:     { label: 'Not Reviewed',    className: 'bg-gray-100 text-gray-700 border-gray-200' },
  reviewed:         { label: 'Reviewed',         className: 'bg-green-100 text-green-800 border-green-200' },
  flagged:          { label: 'Flagged',          className: 'bg-red-100 text-red-800 border-red-200' },
  pending_evidence: { label: 'Pending Evidence', className: 'bg-purple-100 text-purple-800 border-purple-200' },
};

const severityConfig: Record<FindingSeverity, { label: string; className: string }> = {
  critical: { label: 'Critical', className: 'bg-red-100 text-red-800 border-red-200' },
  high:     { label: 'High',     className: 'bg-orange-100 text-orange-800 border-orange-200' },
  medium:   { label: 'Medium',   className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  low:      { label: 'Low',      className: 'bg-blue-100 text-blue-800 border-blue-200' },
};

const findingStatusConfig: Record<FindingStatus, { label: string; className: string }> = {
  open:        { label: 'Open',        className: 'bg-red-100 text-red-800 border-red-200' },
  in_progress: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  resolved:    { label: 'Resolved',    className: 'bg-green-100 text-green-800 border-green-200' },
};

const policyStatusConfig: Record<string, { label: string; className: string }> = {
  approved:         { label: 'Approved',        className: 'bg-green-100 text-green-800 border-green-200' },
  draft:            { label: 'Draft',            className: 'bg-gray-100 text-gray-700 border-gray-200' },
  under_review:     { label: 'Under Review',     className: 'bg-blue-100 text-blue-800 border-blue-200' },
  pending_approval: { label: 'Pending Approval', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  rejected:         { label: 'Rejected',         className: 'bg-red-100 text-red-800 border-red-200' },
};

const taskPriorityConfig: Record<string, { label: string; className: string }> = {
  p0: { label: 'P0 Critical', className: 'bg-red-100 text-red-800 border-red-200' },
  p1: { label: 'P1 High',     className: 'bg-orange-100 text-orange-800 border-orange-200' },
  p2: { label: 'P2 Medium',   className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  p3: { label: 'P3 Low',      className: 'bg-blue-100 text-blue-800 border-blue-200' },
};

const taskStatusConfig: Record<string, { label: string; className: string }> = {
  not_started: { label: 'Not Started', className: 'bg-gray-100 text-gray-700 border-gray-200' },
  in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  at_risk:     { label: 'At Risk',     className: 'bg-orange-100 text-orange-800 border-orange-200' },
  blocked:     { label: 'Blocked',     className: 'bg-red-100 text-red-800 border-red-200' },
  complete:    { label: 'Complete',    className: 'bg-green-100 text-green-800 border-green-200' },
};

const cycleStatusConfig: Record<CycleStatus, { label: string; className: string }> = {
  planned:   { label: 'Planned',   className: 'bg-gray-100 text-gray-700 border-gray-200' },
  active:    { label: 'Active',    className: 'bg-green-100 text-green-800 border-green-200' },
  completed: { label: 'Completed', className: 'bg-blue-100 text-blue-800 border-blue-200' },
};

const severityToPriority: Record<FindingSeverity, ComplianceTask['priority']> = {
  critical: 'p0',
  high: 'p1',
  medium: 'p2',
  low: 'p3',
};

function ReviewBadge({ status }: { status: ReviewStatus }) {
  const cfg = reviewStatusConfig[status];
  return <Badge className={`text-xs ${cfg.className}`} variant="outline">{cfg.label}</Badge>;
}

// ─── Data types ───────────────────────────────────────────────────────────────

interface AuditablePolicy {
  id: string; title: string; department: string; status: string;
  updated_at: string; owner: string; program_id: string | null;
  program_name: string | null; framework: string | null; control_ids: string[];
}

interface AuditableTask {
  id: string; title: string; priority: string; assignee: string;
  department: string; due_date: string | null; status: string;
  program_id: string | null; program_name: string | null; framework: string | null;
  program_phase: string | null; control_id: string | null; control_code: string | null;
}

interface AuditableControl {
  id: string;
  program_control_id: string;
  program_id: string;
  program_name: string;
  framework: string;
  control_id: string;
  title: string;
  domain: string;
  status: string;
  evidence_ready: number;
  evidence_total: number;
  linked_policy_count: number;
}

interface AuditableEvidence {
  id: string;
  program_id: string;
  program_name: string;
  framework: string;
  control_id: string;
  control_code: string;
  title: string;
  evidence_type: string;
  due_date: string | null;
  status: string;
  owner: string;
}

interface SupabaseQueryResult<T> {
  data: T[] | null;
  error: unknown;
}

interface SupabaseQuery<T> extends PromiseLike<SupabaseQueryResult<T>> {
  select(columns: string): SupabaseQuery<T>;
  eq(column: string, value: unknown): SupabaseQuery<T>;
  order(column: string, options?: Record<string, unknown>): SupabaseQuery<T>;
}

type SupabaseUntyped = {
  from: <T>(table: string) => SupabaseQuery<T>;
};

const auditDb = supabase as unknown as SupabaseUntyped;

interface ProgramRef {
  id: string;
  name: string;
  framework: string;
}

interface FrameworkControlRef {
  id: string;
  control_id: string;
  title: string;
  domain?: string | null;
}

interface ProfileNameRef {
  name?: string | null;
}

interface AuditPolicyRow {
  id: string;
  title: string;
  department: string | null;
  status: string;
  updated_at: string;
  program_id: string | null;
  creator?: ProfileNameRef | null;
  program?: ProgramRef | null;
}

interface AuditTaskRow {
  id: string;
  title: string;
  priority: string;
  status: string;
  due_date: string | null;
  assignee?: ProfileNameRef | null;
  department?: ProfileNameRef | null;
  program?: ProgramRef | null;
  control?: FrameworkControlRef | null;
  program_id: string | null;
  program_phase: string | null;
  control_id: string | null;
}

interface AuditProgramControlRow {
  id: string;
  program_id: string;
  status: string;
  control?: FrameworkControlRef | null;
  program?: ProgramRef | null;
}

interface AuditEvidenceRow {
  id: string;
  program_id: string;
  control_id: string;
  title: string;
  evidence_type: string;
  due_date: string | null;
  status: string;
  owner?: ProfileNameRef | null;
  program?: ProgramRef | null;
  control?: FrameworkControlRef | null;
}

interface AuditPolicyControlMappingRow {
  policy_id: string;
  program_id: string;
  control?: Pick<FrameworkControlRef, 'id' | 'control_id'> | null;
}

type ModalState =
  | { type: 'raise_finding'; itemId: string; itemType: AuditItemType; label: string; programId?: string | null; controlId?: string | null; evidenceItemId?: string | null }
  | { type: 'request_evidence'; itemId: string; itemType: AuditItemType; label: string; programId?: string | null; controlId?: string | null; evidenceItemId?: string | null }
  | { type: 'update_finding'; finding: AuditFinding }
  | { type: 'new_finding' }
  | null;

// ─── New Cycle Dialog ─────────────────────────────────────────────────────────

function NewCycleDialog({ open, onClose, onCreate }: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string, description: string, scheduledDate: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onCreate(name.trim(), description.trim(), scheduledDate);
      setName(''); setDescription(''); setScheduledDate('');
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-blue-500" />
            New Audit Cycle
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="cycle-name">Name</Label>
            <Input id="cycle-name" placeholder="e.g. Q2 2026 Internal Audit" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cycle-desc">Description</Label>
            <Textarea id="cycle-desc" placeholder="Scope and objectives of this audit cycle..." value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cycle-date">Scheduled Date</Label>
            <Input id="cycle-date" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!name.trim() || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Create Cycle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AuditorDashboard() {
  const { profile } = useAuth();
  const {
    findings, cycles, profiles, loading: findingsLoading,
    raiseFinding, updateFinding, requestEvidence, upsertReviewStatus,
    getReviewStatus, createCycle, updateCycleStatus,
  } = useAuditFindings();
  const { createTask } = useComplianceTasks();
  const { programs } = useCompliancePrograms();

  const [policies, setPolicies] = useState<AuditablePolicy[]>([]);
  const [tasks, setTasks] = useState<AuditableTask[]>([]);
  const [controls, setControls] = useState<AuditableControl[]>([]);
  const [evidence, setEvidence] = useState<AuditableEvidence[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [modal, setModal] = useState<ModalState>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [myFindingsOnly, setMyFindingsOnly] = useState(false);
  const [cycleFilter, setCycleFilter] = useState<string>('all');
  const [programFilter, setProgramFilter] = useState<string>('all');
  const [isNewCycleOpen, setIsNewCycleOpen] = useState(false);
  const [taskPrefill, setTaskPrefill] = useState<Partial<ComplianceTask> | null>(null);

  const fetchPoliciesAndTasks = useCallback(async () => {
    if (!profile?.company_id) return;
    setDataLoading(true);
    try {
      const [policiesRes, tasksRes, controlsRes, evidenceRes, mappingsRes] = await Promise.all([
        auditDb.from<AuditPolicyRow>('policies')
          .select('id, title, department, status, updated_at, program_id, creator:profiles!policies_created_by_fkey(name), program:programs(id,name,framework)')
          .eq('company_id', profile.company_id)
          .order('updated_at', { ascending: false }),
        auditDb.from<AuditTaskRow>('compliance_tasks')
          .select(`id, title, priority, status, due_date,
            assignee:profiles!compliance_tasks_primary_assignee_id_fkey(name),
            department:departments!compliance_tasks_department_id_fkey(name),
            program:programs!compliance_tasks_program_id_fkey(id,name,framework),
            control:framework_controls!compliance_tasks_control_id_fkey(id,control_id,title),
            program_id, program_phase, control_id`)
          .eq('company_id', profile.company_id)
          .order('created_at', { ascending: false }),
        auditDb.from<AuditProgramControlRow>('program_controls')
          .select('id, program_id, status, control:framework_controls(*), program:programs(id,name,framework)')
          .eq('company_id', profile.company_id),
        auditDb.from<AuditEvidenceRow>('program_evidence_items')
          .select('id, program_id, control_id, title, evidence_type, due_date, status, owner:profiles!program_evidence_items_owner_id_fkey(name), program:programs(id,name,framework), control:framework_controls(id,control_id,title)')
          .eq('company_id', profile.company_id)
          .order('due_date', { ascending: true, nullsFirst: false }),
        auditDb.from<AuditPolicyControlMappingRow>('policy_control_mappings')
          .select('policy_id, program_id, control:framework_controls(id,control_id)')
          .eq('company_id', profile.company_id),
      ]);

      if (policiesRes.error) throw policiesRes.error;
      if (tasksRes.error) throw tasksRes.error;
      if (controlsRes.error) throw controlsRes.error;
      if (evidenceRes.error) throw evidenceRes.error;
      if (mappingsRes.error) throw mappingsRes.error;

      const mappingsByPolicy = new Map<string, string[]>();
      (mappingsRes.data || []).forEach((mapping) => {
        const list = mappingsByPolicy.get(mapping.policy_id) || [];
        if (mapping.control?.control_id) list.push(mapping.control.control_id);
        mappingsByPolicy.set(mapping.policy_id, list);
      });

      const evidenceByControl = new Map<string, AuditEvidenceRow[]>();
      (evidenceRes.data || []).forEach((item) => {
        const list = evidenceByControl.get(item.control_id) || [];
        list.push(item);
        evidenceByControl.set(item.control_id, list);
      });

      const policyCountByControl = new Map<string, number>();
      (mappingsRes.data || []).forEach((mapping) => {
        const controlId = mapping.control?.id;
        if (!controlId) return;
        policyCountByControl.set(controlId, (policyCountByControl.get(controlId) || 0) + 1);
      });

      setPolicies((policiesRes.data || []).map((p) => ({
        id: p.id, title: p.title, department: p.department || 'General',
        status: p.status, updated_at: new Date(p.updated_at).toLocaleDateString(),
        owner: p.creator?.name || '—',
        program_id: p.program_id || null,
        program_name: p.program?.name || null,
        framework: p.program?.framework || null,
        control_ids: mappingsByPolicy.get(p.id) || [],
      })));
      setTasks((tasksRes.data || []).map((t) => ({
        id: t.id, title: t.title, priority: t.priority, status: t.status,
        due_date: t.due_date, assignee: t.assignee?.name || 'Unassigned',
        department: t.department?.name || 'General',
        program_id: t.program_id || null,
        program_name: t.program?.name || null,
        framework: t.program?.framework || null,
        program_phase: t.program_phase || null,
        control_id: t.control_id || null,
        control_code: t.control?.control_id || null,
      })));
      setControls((controlsRes.data || []).map((pc) => {
        const controlEvidence = evidenceByControl.get(pc.control?.id) || [];
        return {
          id: pc.control?.id || pc.id,
          program_control_id: pc.id,
          program_id: pc.program_id,
          program_name: pc.program?.name || 'Program',
          framework: pc.program?.framework || '',
          control_id: pc.control?.control_id || '',
          title: pc.control?.title || '',
          domain: pc.control?.domain || 'General',
          status: pc.status,
          evidence_ready: controlEvidence.filter((item) => ['uploaded', 'linked', 'accepted'].includes(item.status)).length,
          evidence_total: controlEvidence.length,
          linked_policy_count: policyCountByControl.get(pc.control?.id) || 0,
        };
      }));
      setEvidence((evidenceRes.data || []).map((item) => ({
        id: item.id,
        program_id: item.program_id,
        program_name: item.program?.name || 'Program',
        framework: item.program?.framework || '',
        control_id: item.control_id,
        control_code: item.control?.control_id || '',
        title: item.title,
        evidence_type: item.evidence_type,
        due_date: item.due_date,
        status: item.status,
        owner: item.owner?.name || 'Unassigned',
      })));
    } catch (err) {
      console.error('Error fetching audit data:', err);
    } finally {
      setDataLoading(false);
    }
  }, [profile?.company_id]);

  useEffect(() => { fetchPoliciesAndTasks(); }, [fetchPoliciesAndTasks]);

  // ── Derived stats ──────────────────────────────────────────────────────────

  const filteredPolicies = policies.filter((item) => programFilter === 'all' || item.program_id === programFilter);
  const filteredTasks = tasks.filter((item) => programFilter === 'all' || item.program_id === programFilter);
  const filteredControls = controls.filter((item) => programFilter === 'all' || item.program_id === programFilter);
  const filteredEvidence = evidence.filter((item) => programFilter === 'all' || item.program_id === programFilter);
  const auditableItems = [...filteredPolicies, ...filteredTasks, ...filteredControls, ...filteredEvidence];
  const totalItems = auditableItems.length;
  const reviewedCount = auditableItems.filter((i) => getReviewStatus(i.id) === 'reviewed').length;
  const openFindings = findings.filter((f) => f.status === 'open').length;
  const flaggedCount = auditableItems.filter((i) => getReviewStatus(i.id) === 'flagged').length;
  const pendingEvidenceCount = auditableItems.filter((i) => getReviewStatus(i.id) === 'pending_evidence').length;
  const auditHealthScore = totalItems > 0 ? Math.round((reviewedCount / totalItems) * 100) : 0;

  const visibleFindings = findings.filter((f) => {
    if (myFindingsOnly && f.assigned_to !== profile?.id) return false;
    if (cycleFilter !== 'all' && f.audit_cycle_id !== cycleFilter) return false;
    if (programFilter !== 'all' && f.program_id !== programFilter) return false;
    return true;
  });

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleMarkReviewed = async (
    itemId: string,
    itemType: AuditItemType,
    context?: { programId?: string | null; controlId?: string | null; evidenceItemId?: string | null },
  ) => {
    setActionLoading(itemId);
    try { await upsertReviewStatus(itemId, itemType, 'reviewed', context); }
    finally { setActionLoading(null); }
  };

  const handleRaiseFinding = async (
    severity: FindingSeverity, description: string,
    assignedTo: string | null, cycleId: string | null,
  ) => {
    if (!modal) return;
    const itemId = modal.type === 'raise_finding' ? modal.itemId : null;
    const itemType = modal.type === 'raise_finding' ? modal.itemType : null;
    await raiseFinding({
      relatedItemId: itemId,
      relatedItemType: itemType,
      programId: modal.type === 'raise_finding' ? modal.programId : null,
      controlId: modal.type === 'raise_finding' ? modal.controlId : null,
      evidenceItemId: modal.type === 'raise_finding' ? modal.evidenceItemId : null,
      severity,
      description,
      assignedTo,
      auditCycleId: cycleId,
    });
  };

  const handleRequestEvidence = async (evidenceType: string, description: string, deadline: string) => {
    if (modal?.type !== 'request_evidence') return;
    await requestEvidence({
      relatedItemId: modal.itemId,
      relatedItemType: modal.itemType,
      programId: modal.programId,
      controlId: modal.controlId,
      evidenceItemId: modal.evidenceItemId,
      evidenceType,
      description,
      deadline,
    });
  };

  const handleUpdateFinding = async (id: string, updates: {
    status: FindingStatus; notes: string; assignedTo: string | null;
    immediateAction: string; correctiveAction: string;
  }) => {
    await updateFinding(id, {
      status: updates.status,
      notes: updates.notes,
      assignedTo: updates.assignedTo,
      immediateAction: updates.immediateAction,
      correctiveAction: updates.correctiveAction,
    });
  };

  const handleCreateTaskFromFinding = (finding: AuditFinding) => {
    setTaskPrefill({
      title: `Resolve: ${finding.description.slice(0, 60)}${finding.description.length > 60 ? '…' : ''}`,
      description: [
        finding.immediate_action ? `Immediate action: ${finding.immediate_action}` : '',
        finding.corrective_action ? `CAPA: ${finding.corrective_action}` : '',
        `Audit finding ID: ${finding.id.slice(0, 8)}`,
      ].filter(Boolean).join('\n\n') || `Corrective action for audit finding: ${finding.description}`,
      priority: severityToPriority[finding.severity],
      status: 'not_started',
      primary_assignee_id: finding.assigned_to || undefined,
      program_id: finding.program_id || undefined,
      control_id: finding.control_id || undefined,
      evidence_item_id: finding.evidence_item_id || undefined,
      program_phase: finding.evidence_item_id ? 'evidence' : finding.control_id ? 'controls' : undefined,
      reference_type: finding.evidence_item_id
        ? 'evidence'
        : finding.control_id
          ? 'control'
          : finding.related_item_type === 'policy' || finding.related_item_type === 'task'
            ? finding.related_item_type
            : undefined,
    });
  };

  const isLoading = dataLoading || findingsLoading;

  // ── Action buttons shared across Policies & Tasks tabs ────────────────────

  const renderItemActions = (
    id: string,
    label: string,
    type: AuditItemType,
    context?: { programId?: string | null; controlId?: string | null; evidenceItemId?: string | null },
  ) => {
    const reviewStatus = getReviewStatus(id);
    return (
      <div className="flex items-center justify-end gap-1">
        <Button size="sm" variant="ghost"
          className="h-7 px-2 text-xs text-green-700 hover:bg-green-50"
          onClick={() => handleMarkReviewed(id, type, context)}
          disabled={reviewStatus === 'reviewed' || actionLoading === id}
        >
          {actionLoading === id
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <CheckCircle className="h-3 w-3 mr-1" />}
          Mark Reviewed
        </Button>
        <Button size="sm" variant="ghost"
          className="h-7 px-2 text-xs text-orange-700 hover:bg-orange-50"
          onClick={() => setModal({ type: 'raise_finding', itemId: id, itemType: type, label, programId: context?.programId, controlId: context?.controlId, evidenceItemId: context?.evidenceItemId })}
        >
          <Flag className="h-3 w-3 mr-1" />Raise Finding
        </Button>
        <Button size="sm" variant="ghost"
          className="h-7 px-2 text-xs text-purple-700 hover:bg-purple-50"
          onClick={() => setModal({ type: 'request_evidence', itemId: id, itemType: type, label, programId: context?.programId, controlId: context?.controlId, evidenceItemId: context?.evidenceItemId })}
        >
          <FolderOpen className="h-3 w-3 mr-1" />Request Evidence
        </Button>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <Tabs defaultValue="overview" className="space-y-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:grid-cols-7">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="controls">
            Controls <span className="ml-1.5 text-xs opacity-60">{filteredControls.length}</span>
          </TabsTrigger>
          <TabsTrigger value="policies">
            Policies <span className="ml-1.5 text-xs opacity-60">{filteredPolicies.length}</span>
          </TabsTrigger>
          <TabsTrigger value="tasks">
            Tasks <span className="ml-1.5 text-xs opacity-60">{filteredTasks.length}</span>
          </TabsTrigger>
          <TabsTrigger value="evidence">
            Evidence <span className="ml-1.5 text-xs opacity-60">{filteredEvidence.length}</span>
          </TabsTrigger>
          <TabsTrigger value="findings">
            Findings
            {openFindings > 0 && (
              <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white">
                {openFindings}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="cycles">
            Cycles <span className="ml-1.5 text-xs opacity-60">{cycles.length}</span>
          </TabsTrigger>
        </TabsList>
          <Select value={programFilter} onValueChange={setProgramFilter}>
            <SelectTrigger className="lg:w-[280px]">
              <SelectValue placeholder="Program scope" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programs</SelectItem>
              {programs.map((program) => (
                <SelectItem key={program.id} value={program.id}>
                  {formatFramework(program.framework)} · {program.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* ── OVERVIEW ───────────────────────────────────────────────────── */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Items Reviewed', value: `${reviewedCount} / ${totalItems}`, icon: CheckCircle, color: 'text-green-500' },
              { label: 'Open Findings', value: openFindings, icon: AlertTriangle, color: 'text-red-500', valueColor: 'text-red-600' },
              { label: 'Items Flagged', value: flaggedCount, icon: Flag, color: 'text-orange-500', valueColor: 'text-orange-600' },
              { label: 'Pending Evidence', value: pendingEvidenceCount, icon: FolderOpen, color: 'text-purple-500', valueColor: 'text-purple-600' },
            ].map(({ label, value, icon: Icon, color, valueColor }) => (
              <Card key={label}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{label}</p>
                      <p className={`text-2xl font-semibold mt-1 ${valueColor ?? ''}`}>{value}</p>
                    </div>
                    <Icon className={`h-8 w-8 ${color}`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" />Audit Coverage</CardTitle>
                <CardDescription>Percentage of items reviewed in this audit cycle</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : (
                  <div className="flex items-center gap-6">
                    <div className={`rounded-full p-6 ${auditHealthScore >= 80 ? 'bg-green-100' : auditHealthScore >= 50 ? 'bg-yellow-100' : 'bg-red-100'}`}>
                      <span className={`text-4xl font-semibold ${auditHealthScore >= 80 ? 'text-green-700' : auditHealthScore >= 50 ? 'text-yellow-700' : 'text-red-700'}`}>
                        {auditHealthScore}%
                      </span>
                    </div>
                    <div className="flex-1 space-y-2">
                      <Progress value={auditHealthScore} className="h-3" />
                      <p className="text-sm text-muted-foreground">
                        {auditHealthScore >= 80 ? 'Good coverage — keep going' : auditHealthScore >= 50 ? 'Moderate coverage — continue reviewing' : 'Low coverage — many items not yet reviewed'}
                      </p>
                      <div className="grid grid-cols-2 gap-2 pt-2 text-xs text-muted-foreground">
                        <span>{filteredPolicies.filter((p) => getReviewStatus(p.id) === 'reviewed').length} / {filteredPolicies.length} policies</span>
                        <span>{filteredControls.filter((c) => getReviewStatus(c.id) === 'reviewed').length} / {filteredControls.length} controls</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" />Recent Findings</CardTitle>
                <CardDescription>Latest findings raised in this audit</CardDescription>
              </CardHeader>
              <CardContent>
                {findingsLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : findings.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No findings raised yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {findings.slice(0, 5).map((f) => (
                      <div key={f.id} className="flex items-start gap-3 p-3 border rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-mono text-muted-foreground">{f.id.slice(0, 8)}</span>
                            <Badge className={`text-xs ${severityConfig[f.severity].className}`} variant="outline">{severityConfig[f.severity].label}</Badge>
                            <Badge className={`text-xs ${findingStatusConfig[f.status].className}`} variant="outline">{findingStatusConfig[f.status].label}</Badge>
                          </div>
                          <p className="text-sm mt-1 truncate">{f.description}</p>
                          {f.assignee && (
                            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                              <User className="h-3 w-3" />{f.assignee.name}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── CONTROLS ───────────────────────────────────────────────────── */}
        <TabsContent value="controls">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" />Program Controls</CardTitle>
              <CardDescription>Review control readiness, linked policies, evidence coverage, and audit exceptions</CardDescription>
            </CardHeader>
            <CardContent>
              {dataLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : filteredControls.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground">No program controls found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Control</TableHead>
                      <TableHead>Program</TableHead>
                      <TableHead>Domain</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Policies</TableHead>
                      <TableHead>Evidence</TableHead>
                      <TableHead>Review Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredControls.map((control) => (
                      <TableRow key={`${control.program_id}-${control.id}`}>
                        <TableCell>
                          <div className="max-w-[260px]">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{control.control_id}</Badge>
                              <span className="font-medium truncate">{control.title}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <Badge variant="secondary">{formatFramework(control.framework)}</Badge>
                            <p className="text-xs text-muted-foreground truncate max-w-[180px]">{control.program_name}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{control.domain}</TableCell>
                        <TableCell><Badge variant={control.status === 'met' ? 'default' : control.status === 'partial' ? 'secondary' : 'outline'}>{control.status.replace('_', ' ')}</Badge></TableCell>
                        <TableCell className="text-sm">{control.linked_policy_count}</TableCell>
                        <TableCell className="text-sm">{control.evidence_ready}/{control.evidence_total}</TableCell>
                        <TableCell><ReviewBadge status={getReviewStatus(control.id)} /></TableCell>
                        <TableCell className="text-right">
                          {renderItemActions(control.id, `${control.control_id} ${control.title}`, 'control', {
                            programId: control.program_id,
                            controlId: control.id,
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── POLICIES ───────────────────────────────────────────────────── */}
        <TabsContent value="policies">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Policies</CardTitle>
              <CardDescription>Review and action each policy in scope</CardDescription>
            </CardHeader>
            <CardContent>
              {dataLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : filteredPolicies.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground">No policies found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Policy Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Program</TableHead>
                      <TableHead>Controls</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Review Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPolicies.map((policy) => (
                      <TableRow key={policy.id}>
                        <TableCell className="font-medium max-w-[200px] truncate">{policy.title}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{policy.department}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${policyStatusConfig[policy.status]?.className}`} variant="outline">
                            {policyStatusConfig[policy.status]?.label ?? policy.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {policy.program_name ? (
                            <div className="space-y-1">
                              <Badge variant="secondary">{policy.framework ? formatFramework(policy.framework) : 'Program'}</Badge>
                              <p className="text-xs text-muted-foreground truncate max-w-[160px]">{policy.program_name}</p>
                            </div>
                          ) : <span className="text-sm text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex max-w-[160px] flex-wrap gap-1">
                            {policy.control_ids.slice(0, 2).map((controlId) => <Badge key={controlId} variant="outline" className="text-xs">{controlId}</Badge>)}
                            {policy.control_ids.length > 2 && <Badge variant="secondary" className="text-xs">+{policy.control_ids.length - 2}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{policy.updated_at}</TableCell>
                        <TableCell className="text-sm">{policy.owner}</TableCell>
                        <TableCell><ReviewBadge status={getReviewStatus(policy.id)} /></TableCell>
                        <TableCell className="text-right">{renderItemActions(policy.id, policy.title, 'policy', { programId: policy.program_id })}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TASKS ──────────────────────────────────────────────────────── */}
        <TabsContent value="tasks">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5" />Compliance Tasks</CardTitle>
              <CardDescription>Review and action each compliance task in scope</CardDescription>
            </CardHeader>
            <CardContent>
              {dataLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : filteredTasks.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground">No compliance tasks found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Assignee</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Program</TableHead>
                      <TableHead>Control</TableHead>
                      <TableHead>Review Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTasks.map((task) => (
                      <TableRow key={task.id}>
                        <TableCell className="font-medium max-w-[180px] truncate">{task.title}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${taskPriorityConfig[task.priority]?.className}`} variant="outline">
                            {taskPriorityConfig[task.priority]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{task.assignee}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{task.department}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {task.due_date ? new Date(task.due_date).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${taskStatusConfig[task.status]?.className}`} variant="outline">
                            {taskStatusConfig[task.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {task.program_name ? (
                            <div className="space-y-1">
                              <Badge variant="secondary">{task.framework ? formatFramework(task.framework) : 'Program'}</Badge>
                              <p className="text-xs text-muted-foreground capitalize">{task.program_phase?.replace('_', ' ') || task.program_name}</p>
                            </div>
                          ) : <span className="text-sm text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>{task.control_code ? <Badge variant="outline" className="text-xs">{task.control_code}</Badge> : <span className="text-sm text-muted-foreground">—</span>}</TableCell>
                        <TableCell><ReviewBadge status={getReviewStatus(task.id)} /></TableCell>
                        <TableCell className="text-right">{renderItemActions(task.id, task.title, 'task', { programId: task.program_id, controlId: task.control_id })}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── EVIDENCE ───────────────────────────────────────────────────── */}
        <TabsContent value="evidence">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FolderOpen className="h-5 w-5" />Evidence</CardTitle>
              <CardDescription>Review evidence readiness and request remediation against program controls</CardDescription>
            </CardHeader>
            <CardContent>
              {dataLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : filteredEvidence.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground">No evidence items found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Evidence</TableHead>
                      <TableHead>Program</TableHead>
                      <TableHead>Control</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Review Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEvidence.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="max-w-[260px]">
                            <p className="font-medium truncate">{item.title}</p>
                            <p className="text-xs text-muted-foreground capitalize">{item.evidence_type.replace('_', ' ')}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <Badge variant="secondary">{formatFramework(item.framework)}</Badge>
                            <p className="text-xs text-muted-foreground truncate max-w-[180px]">{item.program_name}</p>
                          </div>
                        </TableCell>
                        <TableCell>{item.control_code ? <Badge variant="outline" className="text-xs">{item.control_code}</Badge> : <span className="text-sm text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {item.due_date ? new Date(item.due_date).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`text-xs ${
                              ['uploaded', 'linked', 'accepted'].includes(item.status)
                                ? 'bg-green-100 text-green-800 border-green-200'
                                : item.status === 'not_applicable'
                                  ? 'bg-gray-100 text-gray-700 border-gray-200'
                                  : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                            }`}
                            variant="outline"
                          >
                            {item.status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{item.owner}</TableCell>
                        <TableCell><ReviewBadge status={getReviewStatus(item.id)} /></TableCell>
                        <TableCell className="text-right">
                          {renderItemActions(item.id, item.title, 'evidence', {
                            programId: item.program_id,
                            controlId: item.control_id,
                            evidenceItemId: item.id,
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── FINDINGS ───────────────────────────────────────────────────── */}
        <TabsContent value="findings">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5" />Audit Findings</CardTitle>
                  <CardDescription>All findings raised during this audit cycle</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* My Findings toggle */}
                  <Button
                    size="sm"
                    variant={myFindingsOnly ? 'default' : 'outline'}
                    onClick={() => setMyFindingsOnly((v) => !v)}
                    className="h-8 text-xs gap-1.5"
                  >
                    <User className="h-3.5 w-3.5" />
                    My Findings
                  </Button>

                  {/* Cycle filter */}
                  {cycles.length > 0 && (
                    <Select value={cycleFilter} onValueChange={setCycleFilter}>
                      <SelectTrigger className="h-8 text-xs w-[160px]">
                        <ListFilter className="h-3.5 w-3.5 mr-1.5" />
                        <SelectValue placeholder="All cycles" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All cycles</SelectItem>
                        {cycles.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  <Button size="sm" onClick={() => setModal({ type: 'new_finding' })}>
                    <Plus className="h-4 w-4 mr-1.5" />Raise Finding
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {findingsLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : visibleFindings.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p>{myFindingsOnly ? 'No findings assigned to you' : 'No findings raised yet'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead className="max-w-[220px]">Description</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleFindings.map((finding) => (
                      <TableRow key={finding.id}>
                        <TableCell className="font-mono text-xs text-muted-foreground">{finding.id.slice(0, 8)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground capitalize">{finding.related_item_type ?? 'General'}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${severityConfig[finding.severity].className}`} variant="outline">
                            {severityConfig[finding.severity].label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[220px]">
                          <p className="line-clamp-2">{finding.description}</p>
                          {(finding.immediate_action || finding.corrective_action) && (
                            <p className="text-xs text-blue-600 mt-0.5">CAPA documented</p>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {finding.assignee ? (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3 text-muted-foreground" />
                              {finding.assignee.name}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">Unassigned</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${findingStatusConfig[finding.status].className}`} variant="outline">
                            {findingStatusConfig[finding.status].label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(finding.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              onClick={() => setModal({ type: 'update_finding', finding })}>
                              Update
                            </Button>
                            <Button size="sm" variant="ghost"
                              className="h-7 px-2 text-xs text-blue-700 hover:bg-blue-50"
                              onClick={() => handleCreateTaskFromFinding(finding)}>
                              <Plus className="h-3 w-3 mr-1" />Task
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── CYCLES ─────────────────────────────────────────────────────── */}
        <TabsContent value="cycles">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5" />Audit Cycles</CardTitle>
                  <CardDescription>Named audit sessions — group findings by cycle to track audit-over-audit progress</CardDescription>
                </div>
                <Button size="sm" onClick={() => setIsNewCycleOpen(true)}>
                  <Plus className="h-4 w-4 mr-1.5" />New Cycle
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {findingsLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : cycles.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p className="mb-3">No audit cycles yet</p>
                  <Button size="sm" variant="outline" onClick={() => setIsNewCycleOpen(true)}>
                    <Plus className="h-4 w-4 mr-1.5" />Create first cycle
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {cycles.map((cycle) => {
                    const cycleFindings = findings.filter((f) => f.audit_cycle_id === cycle.id);
                    const openCount = cycleFindings.filter((f) => f.status === 'open').length;
                    return (
                      <div key={cycle.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{cycle.name}</span>
                            <Badge className={`text-xs ${cycleStatusConfig[cycle.status].className}`} variant="outline">
                              {cycleStatusConfig[cycle.status].label}
                            </Badge>
                          </div>
                          {cycle.description && (
                            <p className="text-sm text-muted-foreground truncate">{cycle.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                            {cycle.scheduled_date && (
                              <span>Scheduled: {new Date(cycle.scheduled_date).toLocaleDateString()}</span>
                            )}
                            <span>{cycleFindings.length} finding{cycleFindings.length !== 1 ? 's' : ''}</span>
                            {openCount > 0 && (
                              <span className="text-red-600">{openCount} open</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {cycle.status === 'planned' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                              onClick={() => updateCycleStatus(cycle.id, 'active')}>
                              <PlayCircle className="h-3.5 w-3.5" />Start
                            </Button>
                          )}
                          {cycle.status === 'active' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                              onClick={() => updateCycleStatus(cycle.id, 'completed')}>
                              <CheckCircle2 className="h-3.5 w-3.5" />Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      <RaiseFindingModal
        open={modal?.type === 'raise_finding' || modal?.type === 'new_finding'}
        onClose={() => setModal(null)}
        relatedItem={modal?.type === 'raise_finding' ? modal.label : 'General Finding'}
        profiles={profiles}
        cycles={cycles}
        onSubmit={handleRaiseFinding}
      />

      <RequestEvidenceModal
        open={modal?.type === 'request_evidence'}
        onClose={() => setModal(null)}
        relatedItem={modal?.type === 'request_evidence' ? modal.label : ''}
        onSubmit={handleRequestEvidence}
      />

      <UpdateFindingModal
        open={modal?.type === 'update_finding'}
        onClose={() => setModal(null)}
        finding={modal?.type === 'update_finding' ? modal.finding : null}
        profiles={profiles}
        onSubmit={handleUpdateFinding}
      />

      <TaskFormModal
        open={!!taskPrefill}
        onOpenChange={(open) => { if (!open) setTaskPrefill(null); }}
        task={taskPrefill as ComplianceTask | null}
        onSave={async (data) => { await createTask(data); setTaskPrefill(null); }}
      />

      <NewCycleDialog
        open={isNewCycleOpen}
        onClose={() => setIsNewCycleOpen(false)}
        onCreate={createCycle}
      />
    </>
  );
}
